import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formStatus, setFormStatus] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [newsletterStatus, setNewsletterStatus] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'services', 'features', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5dte97dcu97hjrn4at0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
        form.reset();
        setTimeout(() => setFormStatus(null), 5000);
      } else {
        setFormStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      setFormStatus({ type: 'error', message: 'An error occurred. Please try again.' });
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5dte97dcu97hjrn4atg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setNewsletterStatus({ type: 'success', message: 'Subscribed successfully!' });
        form.reset();
        setTimeout(() => setNewsletterStatus(null), 5000);
      } else {
        setNewsletterStatus({ type: 'error', message: 'Subscription failed. Please try again.' });
      }
    } catch (error) {
      setNewsletterStatus({ type: 'error', message: 'An error occurred. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/646a98f2-5210-4fbb-9562-753441b6caeb.png" 
                alt="NOVA S.R.L. Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>NOVA S.R.L.</span>
            </div>
            
            <div className="hidden md:flex items-center gap-10">
              {['home', 'about', 'services', 'features', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`text-sm font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled 
                      ? activeSection === item ? 'text-cyan-500' : 'text-gray-700 hover:text-cyan-500'
                      : activeSection === item ? 'text-cyan-400' : 'text-white hover:text-cyan-400'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className={`px-6 py-2 rounded-full border-2 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                isScrolled 
                  ? 'border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-gray-900'
              }`}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-6xl md:text-7xl font-extrabold text-white leading-tight">
                Secure Your<br />Digital<br />Freedom
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                Experience true online privacy with military-grade encryption. Browse anonymously, access global content, and protect your data from prying eyes.
              </p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full text-lg font-semibold shadow-lg shadow-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/60 transition-all cursor-pointer whitespace-nowrap flex items-center gap-3 w-fit"
              >
                Start Protecting Now
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>

            <div className="relative hidden md:block">
              <div className="relative w-full h-96">
                <img 
                  src="https://public.readdy.ai/ai/img_res/5fb148ae0361d103a3437ae4e4985c4f.jpg"
                  alt="VPN Network Visualization"
                  className="w-full h-full object-contain animate-float"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-cyan-100 text-cyan-600 rounded-full text-sm font-semibold">
                ABOUT NOVA S.R.L.
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Privacy-First<br />VPN Solutions
              </h2>
              
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  At NOVA S.R.L., we believe that online privacy is a fundamental right. Our mission is to provide cutting-edge VPN technology that empowers individuals and businesses to take control of their digital security.
                </p>
                <p>
                  Founded with a commitment to transparency and user privacy, we've built our infrastructure from the ground up with security at its core. Our no-logs policy ensures that your online activities remain completely private.
                </p>
                <p>
                  We leverage the latest encryption protocols and maintain a global network of secure servers to deliver fast, reliable, and anonymous internet access. Your trust is our priority, and we're dedicated to protecting your digital freedom every day.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://readdy.ai/api/search-image?query=Isometric%20illustration%20of%20secure%20server%20infrastructure%20and%20data%20center%20with%20VPN%20protection%2C%20featuring%20modern%20server%20racks%20with%20glowing%20blue%20and%20purple%20lights%2C%20network%20cables%20and%20connections%2C%20shield%20symbols%20representing%20security%2C%20clean%20minimalist%20design%20with%20navy%20cyan%20and%20purple%20gradient%20colors%2C%20professional%20tech%20illustration%20showing%20encrypted%20data%20flow%20and%20secure%20cloud%20storage&width=600&height=700&seq=about-visual-1&orientation=portrait"
                  alt="Secure Server Infrastructure"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>

          {/* Statistics Bar */}
          <div className="mt-20 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-3">
                  256-bit
                </div>
                <div className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
                  ENCRYPTION
                </div>
              </div>
              <div>
                <div className="text-5xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-3">
                  50+
                </div>
                <div className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
                  SERVER LOCATIONS
                </div>
              </div>
              <div>
                <div className="text-5xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-3">
                  24/7
                </div>
                <div className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
                  PROTECTION
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-semibold mb-6">
              OUR SERVICES
            </div>
            <h2 className="text-5xl font-bold text-white mb-6">
              Complete VPN Protection
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Comprehensive security features designed to keep you safe and anonymous online
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Anonymous Browsing',
                description: 'Hide your IP address and browse the internet completely anonymously. Our advanced masking technology ensures your online identity remains private and untraceable by third parties, ISPs, or government agencies.'
              },
              {
                icon: 'ri-global-line',
                title: 'Global Access',
                description: 'Access content from anywhere in the world with our extensive server network. Bypass geo-restrictions and enjoy unrestricted access to your favorite websites, streaming services, and online platforms from any location.'
              },
              {
                icon: 'ri-flashlight-line',
                title: 'High-Speed Connection',
                description: 'Experience blazing-fast VPN speeds without compromising security. Our optimized servers and advanced protocols ensure smooth streaming, gaming, and browsing with minimal latency and maximum bandwidth.'
              },
              {
                icon: 'ri-devices-line',
                title: 'Multi-Device Support',
                description: 'Protect all your devices with a single subscription. Compatible with Windows, Mac, iOS, Android, and Linux. Secure up to 10 devices simultaneously and enjoy seamless protection across your entire digital ecosystem.'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="group bg-slate-800 rounded-2xl p-12 hover:bg-slate-700 transition-all duration-300 border border-transparent hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/20"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {service.description}
                </p>
                <button className="text-cyan-400 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all cursor-pointer whitespace-nowrap">
                  Learn More
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="inline-block px-4 py-2 bg-purple-200 text-purple-700 rounded-full text-sm font-semibold mb-6">
              FEATURES
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Why Choose<br />NOVA VPN
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Large Feature */}
            <div className="md:col-span-2 bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-18 h-18 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-lock-password-line text-4xl text-white"></i>
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                Military-Grade Encryption
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide. This ensures that your online activities, personal information, and sensitive data remain completely secure from hackers, surveillance, and cyber threats.
              </p>
              <div className="relative h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20AES%20256-bit%20encryption%20process%20with%20flowing%20encrypted%20data%20streams%2C%20binary%20code%20patterns%2C%20lock%20and%20key%20symbols%2C%20glowing%20purple%20and%20blue%20gradient%20effects%2C%20digital%20security%20shield%2C%20modern%20minimalist%20tech%20illustration%20showing%20data%20protection%20and%20cybersecurity%20concept%20with%20geometric%20patterns&width=700&height=300&seq=feature-encryption-1&orientation=landscape"
                  alt="Encryption Visualization"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>

            {/* Medium Features */}
            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-file-shield-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                No-Logs Policy
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We don't track, collect, or store any of your online activities. Your privacy is guaranteed with our strict zero-logs policy.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shut-down-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Kill Switch
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Automatic protection that blocks all internet traffic if your VPN connection drops, preventing data leaks.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                DNS Leak Protection
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Advanced DNS leak protection ensures your browsing queries are never exposed to your ISP or third parties.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-split-cells-horizontal text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Split Tunneling
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Choose which apps use the VPN and which connect directly, giving you complete control over your traffic.
              </p>
            </div>

            <div className="md:col-span-2 bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-speed-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Unlimited Bandwidth
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Enjoy unlimited data transfer with no throttling, caps, or restrictions. Stream, download, and browse as much as you want without worrying about bandwidth limits or reduced speeds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-purple-200 text-purple-700 rounded-full text-sm font-semibold">
                GET IN TOUCH
              </div>
              
              <h2 className="text-5xl font-bold text-gray-900 leading-tight">
                Ready to Get<br />Started?
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Have questions about our VPN service? Our support team is available 24/7 to help you get started and answer any questions you may have.
              </p>

              <div className="space-y-6 pt-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-cyan-600"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Email</div>
                    <a href="mailto:help@nova-srl.com" className="text-lg font-semibold text-gray-900 hover:text-cyan-600 transition-colors">
                      help@nova-srl.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-cyan-600"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Phone</div>
                    <a href="tel:+18624539227" className="text-lg font-semibold text-gray-900 hover:text-cyan-600 transition-colors">
                      +1 8624539227
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-cyan-600"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Address</div>
                    <div className="text-lg font-semibold text-gray-900">
                      GEORGIAS DEL SUR 100<br />
                      Juan José Castelli, H3705XAA<br />
                      Argentina
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-10">
              <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-600 mb-2 font-semibold">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-600 mb-2 font-semibold">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-600 mb-2 font-semibold">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-600 mb-2 font-semibold">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                </div>

                {formStatus && (
                  <div className={`p-4 rounded-xl ${
                    formStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {formStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl text-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img 
                  src="https://public.readdy.ai/ai/img_res/646a98f2-5210-4fbb-9562-753441b6caeb.png" 
                  alt="NOVA S.R.L. Logo" 
                  className="h-8 w-8 object-contain"
                />
                <span className="text-2xl font-bold">NOVA S.R.L.</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your privacy is our priority
              </p>
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-line text-2xl text-cyan-400 animate-pulse"></i>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <div className="space-y-4">
                {['home', 'about', 'services', 'features', 'contact'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className="block text-gray-400 hover:text-cyan-400 transition-colors capitalize cursor-pointer whitespace-nowrap"
                  >
                    {item === 'home' ? 'Home' : item === 'about' ? 'About Us' : item.charAt(0).toUpperCase() + item.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Legal</h4>
              <div className="space-y-4">
                <a href="#" className="block text-gray-400 hover:text-cyan-400 transition-colors">Privacy Policy</a>
                <a href="#" className="block text-gray-400 hover:text-cyan-400 transition-colors">Terms of Service</a>
                <a href="#" className="block text-gray-400 hover:text-cyan-400 transition-colors">Cookie Policy</a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Stay Updated</h4>
              <p className="text-gray-400 text-sm mb-6">Get security tips and updates</p>
              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="flex">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Your email"
                  className="flex-1 px-4 py-3 bg-slate-800 rounded-l-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <button
                  type="submit"
                  className="w-12 h-12 bg-cyan-500 rounded-r-xl flex items-center justify-center hover:bg-cyan-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </button>
              </form>
              {newsletterStatus && (
                <div className={`mt-3 text-sm ${
                  newsletterStatus.type === 'success' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {newsletterStatus.message}
                </div>
              )}
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2025 NOVA S.R.L. All rights reserved
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 text-sm hover:text-cyan-400 transition-colors"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}